import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../store/auth';
import { Brand } from './Brand';
import { Portal } from './Portal';
import StoreSelector from './StoreSelector';

export type AppSection = 'stores' | 'configs' | 'qa' | 'feeds' | 'timeline' | 'platform' | 'usage';

const NAV_ITEMS: Array<{ key: AppSection; to: string; label: string; admin?: boolean }> = [
  { key: 'stores', to: '/stores', label: 'Stores' },
  { key: 'configs', to: '/', label: 'Designs' },
  { key: 'qa', to: '/cutout-qa', label: 'Cutout QA' },
  { key: 'feeds', to: '/feeds', label: 'Feeds' },
  { key: 'timeline', to: '/timeline', label: 'Timeline' },
  { key: 'platform', to: '/platform', label: 'Platform', admin: true },
  { key: 'usage', to: '/admin/usage', label: 'Usage', admin: true },
];

interface AppHeaderProps {
  active: AppSection;
  actions?: ReactNode;
}

export function AppHeader({ active, actions }: AppHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const me = useAuth((s) => s.me);
  const clearAuth = useAuth((s) => s.clear);
  const items = NAV_ITEMS.filter((item) => !item.admin || me?.role === 'admin');

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  async function signOut() {
    try {
      await api.logout();
    } finally {
      clearAuth();
      window.location.href = '/login';
    }
  }

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-ff-line bg-ff-paper/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-5 px-6 py-4 sm:px-8">
          <Brand compact />

          <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary navigation">
            {items.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                aria-current={active === item.key ? 'page' : undefined}
                className={`rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                  active === item.key
                    ? 'bg-ff-ink text-white'
                    : 'text-ff-mute hover:bg-white hover:text-ff-ink'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-2 xl:flex">
            <StoreSelector />
            {actions}
            {me && (
              <span
                className="hidden max-w-44 truncate text-xs text-ff-mute 2xl:inline"
                title={`Signed in as ${me.email} (${me.role})`}
                data-testid="identity"
              >
                {me.email}<span className="text-ff-mute/60"> · {me.role}</span>
              </span>
            )}
            <button
              type="button"
              onClick={signOut}
              className="ff-btn px-3 py-2 text-ff-mute hover:bg-white hover:text-ff-ink"
              data-testid="signout"
            >
              Sign out
            </button>
          </div>

          <div className="flex items-center gap-2 xl:hidden">
            <StoreSelector compact />
            <button
              type="button"
              className="ff-icon-btn"
              aria-expanded={menuOpen}
              aria-controls="app-mobile-menu"
              aria-label="Open navigation"
              onClick={() => setMenuOpen(true)}
            >
              <span className="flex w-4 flex-col gap-1" aria-hidden="true">
                <span className="h-0.5 w-full rounded-full bg-current" />
                <span className="h-0.5 w-full rounded-full bg-current" />
                <span className="h-0.5 w-full rounded-full bg-current" />
              </span>
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <Portal>
          <div
            className="fixed inset-0 z-[70] bg-ff-ink/35 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          >
            <aside
              id="app-mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              className="ml-auto flex h-full w-[min(90vw,24rem)] flex-col bg-white p-6 shadow-hover animate-rise"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <Brand compact onClick={() => setMenuOpen(false)} />
                <button
                  type="button"
                  className="ff-icon-btn"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close navigation"
                >
                  ✕
                </button>
              </div>

              <nav className="mt-10 flex flex-col gap-2" aria-label="Mobile navigation">
                {items.map((item) => (
                  <Link
                    key={item.key}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    aria-current={active === item.key ? 'page' : undefined}
                    className={`flex items-center justify-between rounded-xl px-4 py-3 text-base font-semibold ${
                      active === item.key
                        ? 'bg-ff-ink text-white'
                        : 'text-ff-ink hover:bg-ff-paper'
                    }`}
                  >
                    {item.label}
                    <span aria-hidden="true">→</span>
                  </Link>
                ))}
              </nav>

              {actions && <div className="mt-6 border-t border-ff-line pt-6">{actions}</div>}

              <div className="mt-auto border-t border-ff-line pt-5">
                {me && (
                  <p className="mb-3 truncate text-xs text-ff-mute">
                    {me.email} · {me.role}
                  </p>
                )}
                <button
                  type="button"
                  onClick={signOut}
                  className="ff-btn-ghost w-full"
                  data-testid="mobile-signout"
                >
                  Sign out
                </button>
              </div>
            </aside>
          </div>
        </Portal>
      )}
    </>
  );
}
